// Warms the self-hosted Scanbot WASM engine in the background at app startup so
// document scanning does not have to download ~16MB inside the 50s scan timer,
// and so a weak connection can finish the download while the user reads the
// guideline screens instead of racing the scan.
//
// IMPORTANT: this does NOT call ScanbotSDK.initialize(), so it does NOT start
// the Scanbot 60-second trial clock. It only fetches static engine files. Two
// complementary layers:
//   1. A service worker that durably caches /scanbot-engine/* (cache-first;
//      survives reloads, works offline on repeat visits).
//   2. A background fetch of the simd engine files to start the download early
//      and warm the current session's cache.
//
// Everything is best-effort and guarded: any failure is swallowed so this can
// never break app startup or scanning (which still falls back to a normal
// on-demand network load of the engine).

const ENGINE_PATH = "/scanbot-engine/";
const SW_URL = "/scanbot-engine-sw.js";
const MANIFEST_URL = `${ENGINE_PATH}engine-manifest.json`;
const MAX_ATTEMPTS = 4;

let started = false;

function registerServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.register(SW_URL).catch(() => {
    // SW unsupported/blocked; the background fetch below still helps this session.
  });
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithRetry(url) {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      const res = await fetch(url, { credentials: "omit" });
      if (res && res.ok) {
        // Drain the body so the file is fully downloaded (and cached) end to end.
        await res.arrayBuffer();
        return true;
      }
    } catch (e) {
      // Network hiccup; fall through to backoff and retry.
    }
    if (attempt < MAX_ATTEMPTS) {
      // Backoff 1s, 2s, 4s. This runs in the background and is not gated by any
      // scan timer, so it can afford to wait out a flaky connection.
      await delay(1000 * 2 ** (attempt - 1));
    }
  }
  return false;
}

async function warmEngine() {
  let manifest;
  try {
    const res = await fetch(MANIFEST_URL, { cache: "no-cache" });
    if (!res.ok) return;
    manifest = await res.json();
  } catch (e) {
    return; // engine assets not deployed / offline: nothing to warm
  }
  const suffix = manifest.suffix || "";
  const files = Array.isArray(manifest.preload) ? manifest.preload : [];
  // Download sequentially so a weak connection is not saturated by parallel
  // multi-MB pulls; the simd variant (actually used) is listed first.
  for (const file of files) {
    // eslint-disable-next-line no-await-in-loop
    await fetchWithRetry(ENGINE_PATH + file + suffix);
  }
}

// Kicks off the service-worker registration and background engine warm.
// Safe to call more than once; only the first call does anything.
export function preloadScanbotEngine() {
  if (started) return;
  started = true;

  registerServiceWorker();

  const kick = () => {
    warmEngine().catch(() => {});
  };
  // Defer so the warm never competes with first paint / app bootstrap.
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(kick, { timeout: 3000 });
  } else {
    setTimeout(kick, 1500);
  }
}
